import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosInstance";
import GeneralTable from "../../../components/Table/GeneralTable";
import "./Reimbursements.scss";
import IconButton from "../../../components/IconButton/IconButton";

function Reimbursements() {
  const [reimbursementType, setReimbursementType] = useState("");
  const [limitAmount, setLimitAmount] = useState("");
  const [reimbursements, setReimbursements] = useState([]);
  const [editingRow, setEditingRow] = useState(null);

  // Fetch all reimbursements
  const fetchReimbursements = () => {
    axiosInstance
      .get(`${process.env.REACT_APP_API_BASE_URL}/reimbursements`)
      .then((response) => {
        const reimbursementsData = response.data?.data || [];
        setReimbursements(reimbursementsData);
      })
      .catch((error) => console.error("Error fetching reimbursements:", error));
  };

  useEffect(() => {
    fetchReimbursements();
  }, []);

  // Add a new reimbursement
  const handleAddReimbursement = () => {
    if (reimbursementType.trim() && limitAmount.trim()) {
      const newReimbursement = {
        type: reimbursementType,
        limitAmount: parseFloat(limitAmount),
        companyId: 1,
        status: "ENABLED",
      };

      axiosInstance
        .post(
          `${process.env.REACT_APP_API_BASE_URL}/reimbursements`,
          newReimbursement,
        )
        .then((response) => {
          fetchReimbursements(); // Refresh after successful addition
          setReimbursementType("");
          setLimitAmount("");
        })
        .catch((error) => console.error("Error adding reimbursement:", error));
    }
  };

  // Handle edit button click
  const handleEdit = (id) => {
    setEditingRow(id);
  };

  // Handle save after editing
  const handleSave = (id) => {
    const updatedReimbursement = reimbursements.find(
      (item) => item.reimbursementId === id,
    );

    const payload = {
      type: updatedReimbursement.type || "",
      limitAmount: parseFloat(updatedReimbursement.limitAmount) || 0,
      companyId: updatedReimbursement.companyId || 1,
      status: updatedReimbursement.status || "ENABLED",
    };

    axiosInstance
      .put(
        `${process.env.REACT_APP_API_BASE_URL}/reimbursements/${id}`,
        payload,
      )
      .then(() => {
        setEditingRow(null);
      })
      .catch((error) =>
        console.error(`Error updating reimbursement with id ${id}:`, error),
      );
  };

  // Handle input changes for the editable row
  const handleRowChange = (id, field, value) => {
    setReimbursements((prevReimbursements) =>
      prevReimbursements.map((item) =>
        item.reimbursementId === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  // Define table columns
  const tableColumns = [
    { header: "Type", accessor: "type" },
    { header: "Limit Amount", accessor: "formattedLimitAmount" },
    { header: "Actions", accessor: "actions" },
  ];

  // Transform data for the GeneralTable
  const enhancedReimbursements = reimbursements.map((item) => ({
    ...item,
    formattedLimitAmount:
      editingRow === item.reimbursementId ? (
        <input
          type="number"
          value={item.limitAmount || ""}
          onChange={(e) =>
            handleRowChange(item.reimbursementId, "limitAmount", e.target.value)
          }
          className="table-input"
        />
      ) : (
        `₹${item.limitAmount?.toLocaleString() || "0"}`
      ),
    type:
      editingRow === item.reimbursementId ? (
        <input
          type="text"
          value={item.type || ""}
          onChange={(e) =>
            handleRowChange(item.reimbursementId, "type", e.target.value)
          }
          className="table-input"
        />
      ) : (
        item.type
      ),
    actions: (
      <div className="action-buttons">
        {editingRow === item.reimbursementId ? (
          <>
            <IconButton
              className="icon-button--add"
              onClick={() => handleSave(item.reimbursementId)}
              icon="material-symbols-light:add"
            />
            <IconButton
              className="icon-button--delete"
              onClick={() => setEditingRow(null)}
              icon="material-symbols-light:cancel"
            />
          </>
        ) : (
          <IconButton
            className="icon-button--edit"
            onClick={() => handleEdit(item.reimbursementId)}
            icon="iconamoon:edit-light"
            title="Edit Reimbursements"
          />
        )}
      </div>
    ),
  }));

  return (
    <div className="reimbursements-container">
      <div className="header">
        <h1 className="display6">Reimbursements Management</h1>
      </div>

      <div className="form-container">
        <div className="form-group">
          <label htmlFor="reimbursementType">Reimbursement Type</label>
          <input
            id="reimbursementType"
            type="text"
            value={reimbursementType}
            onChange={(e) => setReimbursementType(e.target.value)}
            placeholder="e.g., Travel, Medical"
          />
        </div>

        <div className="form-group">
          <label htmlFor="limitAmount">Limit Amount</label>
          <input
            id="limitAmount"
            type="number"
            value={limitAmount}
            onChange={(e) => setLimitAmount(e.target.value)}
            placeholder="e.g., 5000"
          />
        </div>
        <div className="form-group">
          <button onClick={handleAddReimbursement} className="add-button">
            Add
          </button>
        </div>
      </div>

      {/* Use GeneralTable instead of custom table */}
      <GeneralTable
        columns={tableColumns}
        data={enhancedReimbursements}
        title="Reimbursement Types"
      />
    </div>
  );
}

export default Reimbursements;
